package com.eladfinish.bible.contest.screens.completeVerse.usecases

import com.eladfinish.bible.contest.common.ChaptersRepository
import com.eladfinish.bible.contest.common.ChaptersRepository.Companion.migrateFilename
import com.eladfinish.bible.contest.common.usecases.VersePickTarget
import com.eladfinish.bible.contest.constants.Constants
import com.eladfinish.bible.contest.constants.StringConstants
import com.eladfinish.bible.contest.constants.StringConstants.COLON_SPLITTER
import com.eladfinish.bible.contest.data.common.AssetReader
import com.eladfinish.bible.contest.screens.mikud.MikudRepository
import com.eladfinish.bible.contest.screens.mikud.usecases.GetRandomBookRandomChapterFilenameUseCase
import com.eladfinish.bible.contest.screens.mikud.usecases.PickRandomChapterUseCase
import com.eladfinish.bible.contest.screens.search.model.VerseUiModel
import com.eladfinish.bible.contest.screens.search.usecases.PickVerseFromChapterUseCaseV2
import com.russhwolf.settings.Settings

open class GetRandomVerseUseCase(
    private val mikudRepository: MikudRepository,
    private val assetReader: AssetReader,
    private val settings: Settings,
    private val pickVerseFromChapterUseCaseV2: PickVerseFromChapterUseCaseV2,
    private val pickRandomChapterUseCase: PickRandomChapterUseCase,
    private val getRandomBookRandomChapterFilenameUseCase: GetRandomBookRandomChapterFilenameUseCase,
) {

    /**
     * Returns a randomly selected verse as a [VerseUiModel].
     * The [VerseUiModel.text] contains the QRI-only verse content
     * (no ktiv, with no-break spaces before paseq) — suitable for both
     * display and game word-matching.
     *
     * Returns `null` if no verse could be picked (e.g. empty mikud).
     */
    open operator fun invoke(): VerseUiModel? {
        val chapterFilename = if (
            settings.getString(
                key = Constants.PREF_RANDOM_APPROACH,
                defaultValue = Constants.APPROACH_BY_BOOK,
            ) == Constants.APPROACH_BY_BOOK
        ) {
            getRandomBookRandomChapterFilenameUseCase(mikudStr = mikudRepository.mikudList)
        } else {
            pickRandomChapterUseCase(chapterList = mikudRepository.mikudList)
        }.migrateFilename()

        val bookIndex = ChaptersRepository.getBookNumber(chapterFilename)
        val chapterContent = assetReader.readFile(
            fileName = StringConstants.ALL_CHAPTERS + assetReader.fileSeparator + chapterFilename,
        )

        // PickVerseFromChapterUseCaseV2 wraps text in gershaim via withGershaimAndColon().
        // We strip them here: buildPasukWithBlanks() adds its own single set of gershaim
        // for the parchment display, so double-wrapping must be avoided.
        return pickVerseFromChapterUseCaseV2(
            bookIndex = bookIndex,
            chapterContent = chapterContent,
            target = VersePickTarget.Random,
            onlyQri = true,
        )?.stripGershaimAndColon()
    }

    private fun VerseUiModel.stripGershaimAndColon(): VerseUiModel =
        copy(
            text = text
                .removePrefix("\"")
                .removeSuffix("\"")
                .removeSuffix(COLON_SPLITTER)
                .trim(),
        )
}
